import './RouletteLot.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { BottomBar, Button, Header, LottieIcon, ScrollView } from '$uikit';
import { useDispatch, useSelector } from 'react-redux';
import {
  roulettePostIdSelector,
  roulettePrizeSelector,
  rouletteSelector,
} from '$store/roulette';
import { goBack, hideModal } from '$navigation/helper';
import { Attachments, Currency, Viewer } from '$shared/components';
import { useDesktop, useTranslator } from '$hooks';
import { balancePayActions } from '$store/balancePay';
import { BalancePayAction } from '$store/balancePay/interface';
import { RootState } from '$store/rootReducer';
import { AttachType, CurrencyEnum } from '$store/models';
import { ViewerItem } from '$shared/components/Viewer/VieverItem/ViewerItem.interface';
import { attachmentsToViewerItems, formatCurrency } from '$utils';

export const RouletteLot: FC = () => {
  const prize = useSelector(roulettePrizeSelector);
  const t = useTranslator();
  const dispatch = useDispatch();
  const { roulette } = useSelector(rouletteSelector);
  const isDesktop = useDesktop();
  const [activeViewItem, setActiveViewItem] = useState<number | null>(null);

  const postIds = useSelector((state: RootState) =>
    roulettePostIdSelector(state, roulette?.rouletteId || 0),
  );

  useEffect(() => {
    if (!prize || !roulette) goBack();
  }, [prize, roulette]);

  const handleRotate = useCallback(() => {
    goBack();
    roulette &&
      dispatch(
        balancePayActions.open({
          amount: roulette.price,
          action: BalancePayAction.ROULETTE,
          objectId: roulette.rouletteId,
          extraId: postIds,
        }),
      );
  }, [roulette]);

  const viewerItems = useMemo<ViewerItem[]>(() => {
    const attach = prize?.attachments?.filter((a) =>
      [AttachType.PHOTO, AttachType.VIDEO].includes(a.type),
    );
    if (!attach?.length) return [];
    return attachmentsToViewerItems(attach, prize?.decryptKey || '');
  }, []);

  const handleClick = ({ target }) => {
    const item = target.closest('.Collage__item');
    if (item) {
      setActiveViewItem(+item.dataset.part);
    }
  };

  return prize && roulette ? (
    <>
      <Header>{t('roulette_prize_title')}</Header>
      <ScrollView>
        <div className="RouletteLot">
          <div className="RouletteLot__attach" onClick={handleClick}>
            <Attachments
              attachments={prize.attachments}
              parentObject={'prize'}
              decryptKey={prize.decryptKey || ''}
            />
            <LottieIcon
              className="RouletteLot__attach__congratulation first"
              name="congratulation"
            />
            <LottieIcon
              className="RouletteLot__attach__congratulation last"
              name="congratulation"
            />
          </div>
          {prize.text && <div className="RouletteLot__text">{prize.text}</div>}
        </div>
      </ScrollView>
      <BottomBar direction="column">
        <Button mode="pay" stretched onClick={handleRotate}>
          {t('roulette_lot_spin_again_button', {
            amount: formatCurrency(roulette.price, CurrencyEnum.EUR),
          })}
        </Button>
        <Button mode="secondary" stretched onClick={() => hideModal()}>
          {t('close')}
        </Button>
      </BottomBar>
      {activeViewItem !== null && (
        <Viewer
          items={viewerItems}
          active={0}
          onChange={setActiveViewItem}
          onClose={() => setActiveViewItem(null)}
          mobile={!isDesktop}
        />
      )}
    </>
  ) : null;
};
